<i18n>
{
	"en": {
		"addFilter": "Add Filter",
		"advancedSearch": "Advanced Search",
		"allLocations": "All Locations",
		"cargoCarrier": "Cargo Carrier",
		"completed": "Completed",
		"confirmOrderRemovalTransporter": "An order you want to remove has already been sent. You will need to contact the transporter to complete the removal. Are you sure you want to remove the selected orders?",
		"confirmOrderRemovalTrip": "Are you sure you want to remove the selected order from trip {tripNumber}?",
		"editSearch": "Edit search",
		"fromDate": "From",
		"fromLocation": "From Location",
		"loading": "Loading...",
		"locations": "Locations",
		"ready": "Ready",
		"resetSearch": "Reset Search",
		"save": "Save",
		"saveSearch": "Save this search",
		"search": "Search",
		"searchForCargoCarrier": "Search for cargo carrier",
		"searchForLocation": "Search for location",
		"searchForTrips": "Search for Trips",
		"selectDate": "Select date",
		"selectTripStatus": "Select status",
		"status": "Status",
		"toDate": "To",
		"toLocation": "To Location",
		"tripStatusSelected": "All statuses | {count} status selected | {count} statuses selected",
		"underway": "Underway",
		"cancelled": "Cancelled",
		"draft": "Draft",
		"received": "Received",
		"rejected": "Rejected",
		"started": "Started",
		"accepted": "Accepted",
		"unloaded": "Unloaded",
		"loaded": "Loaded",
		"planned": "Planned",
		"unsuccessful": "Unsuccessful pickup",
		"readyForPickup": "Ready for pickup",
		"failedToFetch": "Failed to search for trips",
		"selectTripsFromArea": "Select trips from area",
		"selectTripsToArea": "Select trips to area"
	},
	"no": {
		"addFilter": "Legg til filter",
		"advancedSearch": "Avansert søk",
		"allLocations": "Alle steder",
		"cargoCarrier": "Lastebærer",
		"completed": "Fullført",
		"confirmOrderRemovalTransporter": "Oppdraget du vil fjerne, er allerede sendt. Du må kontakte transportøren for å fullføre fjerningen. Er du sikker på at du vil fjerne de valgte oppdrag?",
		"confirmOrderRemovalTrip": "Er du sikker på at du vil fjerne den valgte oppdrag fra turen {tripNumber}?",
		"editSearch": "Rediger dette søket",
		"fromDate": "Fra",
		"fromLocation": "Fra sted",
		"loading": "Henter turer...",
		"locations": "Steder",
		"ready": "Klar",
		"resetSearch": "Nullstill søk",
		"save": "Lagre",
		"saveSearch": "Lagre dette søket",
		"search": "Søk",
		"searchForCargoCarrier": "Søk etter lastebærer",
		"searchForLocation": "Søk etter sted",
		"searchForTrips": "Søk etter Turer",
		"selectDate": "Velg dato",
		"selectTripStatus": "Velg status",
		"status": "Status",
		"toDate": "Til",
		"toLocation": "Til sted",
		"tripStatusSelected": "Alle statuser | {count} status valgt | {count} statuser valgt",
		"underway": "Underveis",
		"cancelled": "Annullert",
		"draft": "Kladd",
		"received": "Mottatt",
		"rejected": "Avslått",
		"started": "Startet",
		"requested": "Forespurt",
		"accepted": "Godtatt",
		"unloaded": "Losset",
		"loaded": "Lastet",
		"planned": "Planlagt",
		"unsuccessful": "Bomtur",
		"readyForPickup": "Klar for lasting",
		"failedToFetch": "Kunne ikke søke etter turer",
		"selectTripsFromArea": "Velg turer fra område",
		"selectTripsToArea": "Velg turer til område"
	}
}
</i18n>
<template>
	<t-vertical-panel>
		<div slot="header" class="trip-search-header">
			<t-header-select
				:items="savedSearches"
				:selectedItem="loadedSearch"
				width="100%"
				icon="search"
				@change="onLoadSavedSearch"
			>
				<template>
					{{ loadedSearch.id === -1 ? $t('searchForTrips') : loadedSearch.label}}
				</template>
				<template v-slot:menuitemlabel="{item}">
					{{ item.labelKey ? $t(item.labelKey) : item.label }}
				</template>
			</t-header-select>
		</div>

		<div slot="sub-header" class="trip-search-sub-header">
			<t-button
				:value="tripSearchText"
				:disabled="tripsLoading"
				appearance="primary"
				width="100%"
				@click="searchTrips"
			/>
			<t-button
				v-if="loadedSearch.id === -1"
				:value="$t('saveSearch')"
				appearance="minimal"
				width="100%"
				type="bp"
				iconBefore="filter-keep"
				@click="showSaveSearch"
			/>
			<t-button
				v-else-if="loadedSearch.id"
				:value="$t('editSearch')"
				appearance="minimal"
				width="100%"
				type="bp"
				iconBefore="filter-keep"
				@click="showSaveSearch"
			/>
		</div>

		<div class="trip-search-content">
			<t-form-group horizontal>
				<t-date-picker
					:label="$t('fromDate')"
					:value="formatDate(searchFilter.etd.from)"
					:dateFormat="displayDateFormat"
					width="100%"
					relative
					@input="setDateFrom"
					@enter="searchTrips"
				/>
				<t-date-picker
					:label="$t('toDate')"
					:value="formatDate(searchFilter.etd.to)"
					:dateFormat="displayDateFormat"
					width="100%"
					relative
					@input="setDateTo"
					@enter="searchTrips"
				/>
			</t-form-group>

			<t-form-group>
				<LocationsCombobox
					ref="fromLocation"
					:label="$t('fromLocation')"
					:placeholder="$t('searchForLocation')"
					:selected="searchFilter.fromLocation"
					:dialogTitle="$t('selectTripsFromArea')"
					disableGlobalAddress
					dialogType="fromLocation"
					width="100%"
					iconSize="sm"
					icon="geosearch"
					@input="inputs.fromLocation = $event"
					@select="onSelectFromLocation"
					@multiselect="setFromLocation"
					@enter="searchTrips"
				/>
				<t-tag-container
					v-if="hasItems(searchFilter.fromLocation)"
					:items="searchFilter.fromLocation"
					displayKey="label"
					@change="setFromLocation"
					@tag-click="changeTagOpCode({ key: 'fromLocation', tag: $event })"
				>
					<template v-slot="{ item }">
						<t-location-tag-inner :item="item" displayKey="label" />
					</template>
				</t-tag-container>
			</t-form-group>
			<t-form-group>
				<LocationsCombobox
					ref="toLocation"
					:label="$t('toLocation')"
					:placeholder="$t('allLocations')"
					:selected="searchFilter.toLocation"
					:dialogTitle="$t('selectTripsToArea')"
					disableGlobalAddress
					dialogType="toLocation"
					width="100%"
					iconSize="sm"
					icon="geosearch"
					@input="inputs.toLocation = $event"
					@select="onSelectToLocation"
					@multiselect="setToLocation"
					@enter="searchTrips"
				/>
				<t-tag-container
					v-if="hasItems(searchFilter.toLocation)"
					:items="searchFilter.toLocation"
					displayKey="label"
					@change="setToLocation"
					@tag-click="changeTagOpCode({ key: 'toLocation', tag: $event })"
				>
					<template v-slot="{ item }">
						<t-location-tag-inner :item="item" displayKey="label" />
					</template>
				</t-tag-container>
			</t-form-group>


			<t-form-group>
				<trip-types-select :selectedItems="searchFilter.tripType" @change="addTripType" />
				<t-tag-container
					v-if="hasItems(searchFilter.tripType)"
					:items="searchFilter.tripType"
					@change="setTripType"
				>
					<template v-slot="{item}">
						{{ item.value }}
					</template>
				</t-tag-container>
			</t-form-group>

			<OperationalStatusFormInput
				:selectedItems="searchFilter.status"
				relative
				@change="addStatus"
			>
				<template v-slot:displayedValue="{popoverShow}">
					{{ popoverShow ? $t('selectTripStatus') : $tc('tripStatusSelected', searchFilter.status.length)}}
				</template>
				<template #after>
					<t-tag-container
						v-if="hasItems(searchFilter.status)"
						:items="searchFilter.status"
						@change="setStatuses"
					>
						<template v-slot="{item}">
							<div style="display: flex; align-items: center; justify-content: flex-start;">
								<t-status-color-dot :color="getStatusColor(item)"/>
								<div style="margin-left: 5px;">{{getStatusText(item)}}</div>
							</div>
						</template>
					</t-tag-container>
				</template>
			</OperationalStatusFormInput>

			<t-form-group>
				<cargo-carriers-search-input
					ref="cargoCarrier"
					:selectedItems="searchFilter.cargoCarrier"
					width="100%"
					@input="inputs.cargoCarrier = $event"
					@change="addCargoCarrierString"
					@select="addCargoCarrier"
					@enter="searchTrips"
					@set:selected="setCargoCarrier"
				/>
				<t-tag-container
					v-if="hasItems(searchFilter.cargoCarrier)"
					:items="searchFilter.cargoCarrier"
					displayKey="description"
					@change="setCargoCarrier"
					@tag-click="changeTagOpCodeBetweenNotAndOr({ key: 'cargoCarrier', tag: $event })"
				>
					<template v-slot="{ item }">
						<t-location-tag-inner :item="item" displayKey="description" />
					</template>
				</t-tag-container>
			</t-form-group>

			<t-form-group :title="$t('advancedSearch')">
				<t-button
					type="bp"
					iconBefore="add"
					appearance="minimal"
				>{{$t('addFilter')}}</t-button>
			</t-form-group>
		</div>
		<div slot="dialogs">
			<SaveSearch
				:isShown="saveSearchVisible"
				:searchFilter="searchFilter"
				:loadedSearch="savedSearch"
				@close="hideSaveSearch"
				@delete="onDeleteSavedSearch"
			/>
		</div>
	</t-vertical-panel>
</template>

<script>
import { mapDynamicState, mapDynamicActions } from '@/core/mapDynamic';
import {
	eventBus, statuses, displayDateFormat, displayDateFormatter
} from '@/core';
import { mapState as savedSearchState } from '@/modules/saveTripSearch/store/saveTripSearch.store.js';
import SaveSearch from '@/modules/saveTripSearch/SaveSearch';
import LocationsCombobox from '@/modules/locations/LocationsCombobox';
import TripTypesSelect from '@/modules/tripTypes/TripTypesSelect';
import CargoCarriersSearchInput from '@/modules/cargoCarriers/CargoCarriersSearchInput';
import OperationalStatusFormInput from '@/modules/operationalStatus/OperationalStatusFormInput.vue';


export default {
	name: 'TripSearchFilters',
	components: {
		SaveSearch,
		LocationsCombobox,
		TripTypesSelect,
		CargoCarriersSearchInput,
		OperationalStatusFormInput,
	},
	data() {
		return {
			saveSearchVisible: false,
			inputs: {
				fromLocation: '',
				toLocation: '',
				cargoCarrier: ''
			}
		};
	},
	computed: {
		...mapDynamicState('computedStoreNamespace', [
			'searchFilter',
			'tripsLoading',
			'statuses',
			'loadedSearch',
		]),
		...savedSearchState(['savedSearches',]),
		tripSearchText() {
			return this.tripsLoading ? this.$t('loading') : this.$t('search');
		},
		searchId() {
			return this.$route.params.searchId || -1;
		},
		savedSearch() {
			if (Array.isArray(this.savedSearches))
				return this.savedSearches.find(x => x.id === this.searchId);
			return {};
		},
		computedStoreNamespace() {
			return `tripSearch/${this.searchId}`;
		},
		displayDateFormat() {
			return displayDateFormat;
		},
	},
	watch: {
		savedSearch(newValue,oldValue) {
			if (this.loadedSearch && this.loadedSearch.id === newValue.id) {
				this.performFetchTrips();
			} else if ( (oldValue === undefined && newValue !== undefined) ||
				(oldValue && newValue && oldValue.id !== newValue.id) ) {
				this.loadSavedAndSearch();
			} else if (newValue === undefined && this.searchId !== -1) {
				this.$router.replace({ name: 'tripSearch' });
			}
		}
	},
	created() {
		eventBus.$on('refreshTripList', this.performFetchTrips);
	},
	mounted() {
		if (!this.savedSearch && this.searchId !== -1) this.$router.replace({ name: 'tripSearch' });
		if (this.searchId !== this.loadedSearch.id)
			this.loadSavedAndSearch();
		else if (this.searchId === this.loadedSearch.id)
			this.performFetchTrips();
	},
	beforeDestroy() {
		eventBus.$off('refreshTripList');
	},
	methods: {
		...mapDynamicActions('computedStoreNamespace', [
			'fetchTrips',
			'loadSavedSearch',
			'setDateFrom',
			'setDateTo',
			'addFromLocation',
			'addFromLocationString',
			'setFromLocation',
			'addToLocation',
			'addToLocationString',
			'setToLocation',
			'addStatus',
			'setStatuses',
			'addTripType',
			'setTripType',
			'setCargoCarrier',
			'addCargoCarrier',
			'addCargoCarrierString',
			'addInputTagsOnSearch',
			'changeTagOpCode',
			'changeTagOpCodeBetweenNotAndOr',
		]),
		searchTrips() {
			this.addInputTagsOnSearch(this.inputs);
			this.$refs.fromLocation.clearInput();
			this.$refs.toLocation.clearInput();
			this.$refs.cargoCarrier.clearInput();
			this.performFetchTrips();
		},
		async performFetchTrips() {
			try {
				await this.fetchTrips();
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToFetch')
				});
			}
		},
		onSelectFromLocation(location) {
			
			if (typeof location === 'string') {
				this.addFromLocationString(location);
				
			} else {
				this.addFromLocation(location);
			}
		},
		onSelectToLocation(location) {
			
			if (typeof location === 'string') {
				this.addToLocationString(location);
				
			} else {
				this.addToLocation(location);
			}
		},
		async onAddCargoCarrier(value) {
			await this.addCargoCarrier(value);
			this.$set(this,'cargoCarrierValue', '');
		},
		showSaveSearch() {
			this.saveSearchVisible = true;
		},
		hideSaveSearch() {
			this.saveSearchVisible = false;
		},
		hasItems(array) {
			return array.length > 0;
		},
		loadSavedAndSearch() {
			this.loadSavedSearch(this.savedSearch);
			this.searchTrips();
		},
		onDeleteSavedSearch() {
			this.hideSaveSearch();
			this.$router.push({ name: 'tripSearch' });
		},
		onLoadSavedSearch(item) {
			if (!item) return;
			if (item.id === -1) {
				this.$router.push({ name: 'tripSearch' });
			} else {
				this.$router.push({
					name: 'savedTripSearch',
					params: { searchId: item.id }
				});
			}
		},
		getStatusText(status) {
			return this.$t(statuses.trip(status));
		},
		getStatusColor(status) {
			return statuses.color(status);
		},
		formatDate(date) {
			return displayDateFormatter(date);
		}
	},
};
</script>

<style lang="scss" scoped>
.trip-search-header {
	display:flex;
	align-items: center;
	height: 100%;
}

.trip-search-sub-header {
	display:inline-grid;
	grid-auto-flow: column;
	grid-auto-columns: 1fr;
	column-gap: 20px;
	padding: 0px 15px;
	height: 100%;
	width: calc(100% - 30px);
	align-items: center;
}

.trip-search-content {
	display: grid;
	grid-auto-rows: minmax(min-content, max-content);
	row-gap: 20px;
	background: white;
	min-height: 100%;
	box-sizing: border-box;
	padding: 10px 15px 10px 15px;
}
</style>
